import { ExternalLink } from 'lucide-react'
import { Suspense, useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { SEGMENTS } from '@/app/router/routes'
import { ChangelogMDXProvider } from '@/shared/components/changelog/mdx-provider'
import { getSortedChangelogs } from '@/shared/constants/changelog-config'
import { WHATS_NEW_CONFIG } from '@/shared/constants/whats-new-config'
import { cn } from '@/shared/lib/utils'
import { Badge } from '@/shared/ui/badge'
import { Button } from '@/shared/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/shared/ui/dialog'

type WhatsNewModalProps = {
  currentVersion?: string
  onClose?: () => void
}

/**
 * En el teléfono el modal ocupa la pantalla entera; desde `sm` es un diálogo centrado. En los dos es
 * una columna: encabezado y pie fijos, y sólo el cuerpo scrollea, así los botones nunca quedan
 * fuera de la pantalla.
 *
 * Se sigue posicionando con el centrado del `DialogContent` (left/top 50% + translate) también en el
 * teléfono: así la animación de entrada del diálogo, que cuenta con ese translate, no hay que
 * reescribirla.
 */
const CONTENT_LAYOUT = cn(
  'flex h-dvh w-full max-w-none flex-col gap-0 overflow-hidden rounded-none border-0 p-0',
  'sm:h-auto sm:max-h-[85dvh] sm:max-w-2xl sm:rounded-xl sm:border'
)

/** La X del `DialogContent`: un blanco táctil de 36 px en vez del ícono pelado de 16. */
const CLOSE_BUTTON = cn(
  '[&>button:last-child]:top-3 [&>button:last-child]:right-3 sm:[&>button:last-child]:top-4 sm:[&>button:last-child]:right-4',
  '[&>button:last-child]:flex [&>button:last-child]:size-9 [&>button:last-child]:items-center [&>button:last-child]:justify-center [&>button:last-child]:rounded-full'
)

const SKELETON_ROWS = ['first', 'second', 'third'] as const

const WhatsNewSkeleton = () => (
  <div className="animate-pulse space-y-4">
    <div className="h-6 bg-gray-200 rounded mb-4" />
    {SKELETON_ROWS.map((row) => (
      <div key={row} className="border rounded-lg p-4">
        <div className="h-4 bg-gray-200 rounded mb-2" />
        <div className="h-3 bg-gray-100 rounded w-3/4" />
      </div>
    ))}
  </div>
)

export const WhatsNewModal = ({
  currentVersion = WHATS_NEW_CONFIG.CURRENT_VERSION,
  onClose
}: WhatsNewModalProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const navigate = useNavigate()

  // Obtener la última versión del changelog
  const latestChangelog = getSortedChangelogs()[0]

  useEffect(() => {
    // Verificar si el usuario ya vio esta versión
    const seenVersion = localStorage.getItem(WHATS_NEW_CONFIG.SETTINGS.STORAGE_KEY)

    if (seenVersion !== currentVersion) {
      // Mostrar modal después de un pequeño delay para mejor UX
      const timer = setTimeout(() => {
        setIsOpen(true)
      }, WHATS_NEW_CONFIG.SETTINGS.SHOW_DELAY)

      return () => clearTimeout(timer)
    }
  }, [currentVersion])

  const handleClose = () => {
    // Marcar esta versión como vista
    localStorage.setItem(WHATS_NEW_CONFIG.SETTINGS.STORAGE_KEY, currentVersion)
    setIsOpen(false)
    onClose?.()
  }

  const handleViewFullChangelog = () => {
    handleClose()
    navigate(`/admin/${SEGMENTS.admin.changelog}#${latestChangelog.data.version}`)
  }

  if (!isOpen) return null

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className={cn(CONTENT_LAYOUT, CLOSE_BUTTON)}>
        <DialogHeader className="shrink-0 flex-row items-center gap-3 space-y-0 border-b py-4 pr-14 pl-5 text-left sm:pl-6">
          <img src="/images/logo.svg" width={36} height={36} className="size-9 shrink-0" alt="" />
          <div className="min-w-0 space-y-0.5">
            <DialogTitle className="text-lg text-primary">¡Hay novedades! 🎉</DialogTitle>
            <DialogDescription>Descubre las últimas mejoras del sistema</DialogDescription>
          </div>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-5 sm:px-6">
          <Suspense fallback={<WhatsNewSkeleton />}>
            {latestChangelog ? (
              <div className="min-w-0 space-y-4 break-words">
                {/* Encabezado de la entrada: título, versión, resumen y etiquetas */}
                <div className="border-b pb-4">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <h3 className="text-lg font-semibold text-foreground">
                      {latestChangelog.data.title}
                    </h3>
                    {latestChangelog.data.version && (
                      <Badge
                        variant="outline"
                        className="bg-primary/10 text-primary border-primary/20"
                      >
                        v{latestChangelog.data.version}
                      </Badge>
                    )}
                  </div>
                  {latestChangelog.data.description && (
                    <p className="text-sm text-muted-foreground mt-1">
                      {latestChangelog.data.description}
                    </p>
                  )}

                  {latestChangelog.data.tags && latestChangelog.data.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {latestChangelog.data.tags.map((tag: string) => (
                        <span
                          key={tag}
                          className="h-6 w-fit px-2 text-xs font-medium bg-muted text-muted-foreground rounded-full border flex items-center justify-center"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Contenido MDX: nada adentro puede ensanchar el modal */}
                <div className="mdx-content [&_img]:h-auto [&_img]:max-w-full [&_video]:max-w-full">
                  <ChangelogMDXProvider>
                    <latestChangelog.data.body />
                  </ChangelogMDXProvider>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                No hay novedades disponibles
              </div>
            )}
          </Suspense>
        </div>

        {/* Una sola acción: «Omitir» hacía lo mismo que «¡Entendido!» (los dos marcan la versión como vista).
            En el teléfono va a ancho completo y el historial queda debajo. */}
        <DialogFooter className="shrink-0 flex-col gap-3 border-t px-5 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:flex-row sm:items-center sm:justify-between sm:space-x-0 sm:px-6 sm:pb-4">
          <Button onClick={handleClose} className="w-full sm:order-2 sm:w-auto">
            ¡Entendido!
          </Button>

          <Link
            to={`/admin/${SEGMENTS.admin.changelog}#${latestChangelog.data.version}`}
            onClick={handleViewFullChangelog}
            className="flex items-center justify-center gap-2 self-center rounded-sm py-1 text-sm text-muted-foreground underline-offset-4 hover:text-primary hover:underline focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none sm:order-1"
          >
            <ExternalLink className="size-4" aria-hidden="true" />
            Historial de cambios
          </Link>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// Hook para usar el modal de novedades
export const useWhatsNewModal = (version?: string) => {
  const [shouldShow, setShouldShow] = useState(false)

  useEffect(() => {
    const checkVersion = version || WHATS_NEW_CONFIG.CURRENT_VERSION
    const seenVersion = localStorage.getItem(WHATS_NEW_CONFIG.SETTINGS.STORAGE_KEY)
    setShouldShow(seenVersion !== checkVersion)
  }, [version])

  const markAsSeen = (version?: string) => {
    const checkVersion = version || WHATS_NEW_CONFIG.CURRENT_VERSION
    localStorage.setItem(WHATS_NEW_CONFIG.SETTINGS.STORAGE_KEY, checkVersion)
    setShouldShow(false)
  }

  return { shouldShow, markAsSeen }
}
